/**
 * International Phone Input Module
 * intl-tel-input initialization with country formatting
 */

import intlTelInput from "intl-tel-input";

const countryFormats = {
  id: {
    pattern: "XXX-XXXX-XXXX",
    format: (num) => num.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3"),
    maxLength: 12,
  },
  us: {
    pattern: "XXX-XXX-XXXX",
    format: (num) => num.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3"),
    maxLength: 10,
  },
  gb: {
    pattern: "XXXX-XXX-XXXX",
    format: (num) => num.replace(/(\d{4})(\d{3})(\d{4})/, "$1-$2-$3"),
    maxLength: 11,
  },
  my: {
    pattern: "XX-XXXX-XXXX",
    format: (num) => num.replace(/(\d{2})(\d{4})(\d{4})/, "$1-$2-$3"),
    maxLength: 10,
  },
  sg: {
    pattern: "XXXX-XXXX",
    format: (num) => num.replace(/(\d{4})(\d{4})/, "$1-$2"),
    maxLength: 8,
  },
};

function formatPhoneNumber(value, countryCode) {
  const digits = value.replace(/\D/g, "");
  const format = countryFormats[countryCode];
  if (!format) return digits;

  const limitedDigits = digits.substring(0, format.maxLength);
  return format.format(limitedDigits);
}

function setupPhoneInput(phoneInput) {
  if (phoneInput.itiInstance) {
    console.log("Phone input already initialized, skipping...");
    return phoneInput.itiInstance;
  }

  const isVisible = phoneInput.offsetParent !== null;
  if (!isVisible) {
    setTimeout(() => setupPhoneInput(phoneInput), 100);
    return;
  }

  const iti = intlTelInput(phoneInput, {
    initialCountry: "id",
    separateDialCode: true,
    preferredCountries: ["id", "us", "gb", "my", "sg"],
    utilsScript:
      "https://cdn.jsdelivr.net/npm/intl-tel-input@18.2.1/build/js/utils.js",
    autoFormat: false,
    autoPlaceholder: "off",
  });

  let isFormatting = false;

  const updatePlaceholder = () => {
    const countryData = iti.getSelectedCountryData();
    const format = countryFormats[countryData.iso2];

    if (format) {
      phoneInput.placeholder = format.pattern;
      console.log(
        `Updated placeholder for ${countryData.iso2}:`,
        format.pattern
      );
    } else {
      phoneInput.placeholder = "Enter phone number";
    }
  };

  const handleInput = (e) => {
    if (isFormatting) return;
    isFormatting = true;

    const countryData = iti.getSelectedCountryData();
    const formattedValue = formatPhoneNumber(e.target.value, countryData.iso2);
    e.target.value = formattedValue;

    isFormatting = false;
  };

  const handleCountryChange = () => {
    console.log("Country changed, updating placeholder...");
    updatePlaceholder();

    if (phoneInput.value) {
      const countryData = iti.getSelectedCountryData();
      phoneInput.value = formatPhoneNumber(phoneInput.value, countryData.iso2);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = (e.clipboardData || window.clipboardData).getData("text");
    const countryData = iti.getSelectedCountryData();
    phoneInput.value = formatPhoneNumber(pastedText, countryData.iso2);
  };

  phoneInput.addEventListener("input", handleInput);
  phoneInput.addEventListener("countrychange", handleCountryChange);
  phoneInput.addEventListener("paste", handlePaste);

  phoneInput._eventHandlers = {
    input: handleInput,
    countrychange: handleCountryChange,
    paste: handlePaste,
  };

  setTimeout(() => {
    updatePlaceholder();
  }, 100);

  phoneInput.itiInstance = iti;
  return iti;
}

export function initIntl(scope = document) {
  const phoneInputs = scope.querySelectorAll(".phone-input");
  console.log(`Found ${phoneInputs.length} phone inputs in scope`);

  phoneInputs.forEach((phoneInput) => {
    try {
      setupPhoneInput(phoneInput);
    } catch (error) {
      console.error("Error initializing phone input:", error);
    }
  });

  // Setup global functions only once
  if (!window.getPhoneData) {
    window.getPhoneData = (inputElement) => {
      if (!inputElement || !inputElement.itiInstance) {
        console.error("Invalid phone input element or not initialized");
        return null;
      }

      const iti = inputElement.itiInstance;
      return {
        formatted: inputElement.value,
        clean: inputElement.value.replace(/\D/g, ""),
        international: iti.getNumber(),
        isValid: iti.isValidNumber(),
        country: iti.getSelectedCountryData(),
      };
    };

    window.getAllPhoneData = () => {
      const allInputs = document.querySelectorAll(".phone-input");
      const allData = [];
      allInputs.forEach((input, index) => {
        const data = window.getPhoneData(input);
        if (data) {
          allData.push({
            index: index,
            element: input,
            id: input.id || `phone-${index}`,
            ...data,
          });
        }
      });
      return allData;
    };

    window.getPhoneDataById = (selector) => {
      const element = document.querySelector(selector);
      return window.getPhoneData(element);
    };
  }
}
